// ============================================================
// 客户端异步任务 handler 注册
// 必须在 App.vue 顶层 import（不能放懒加载路由组件里），
// 保证刷新页面后 asyncTaskStore.restore() 时也能找到对应 handler 渲染通知。
// 轮询接口：POST /customer/asyncTask/batchDetail
//
// 导出 handler 为临时占位，后续会按产品约定重新定义。
// ============================================================
import type { AsyncTaskContext, AsyncTaskDetail } from "@shared/types/asyncTask";
import { ASYNC_TASK_TYPE } from "@shared/types/asyncTask";
import { createDownloadCenterAction, registerAsyncTaskHandler, registerAsyncTaskPollingConfig, type AsyncTaskNotifyAction, type AsyncTaskNotifyContent } from "@shared/utils/asyncTaskHandlers";
import { t } from "@shared/utils/i18n";
import { WAREHOUSE_ASYNC_TASK_TYPE } from "@app/views/warehouse/setting/config/constants";

/** 三个仓库同步类任务共用的轮询节奏：5min 快轮询 → 10min 抽查 → 最长等 2h，超时给失败记录 */
const WAREHOUSE_SYNC_POLLING_CONFIG = {
    activeWindowMs: 5 * 60 * 1000,
    waitCheckIntervalMs: 10 * 60 * 1000,
    maxWaitMs: 2 * 60 * 60 * 1000,
    notifyOnGiveUp: true,
};

/** 客户端下载中心路由（导出落地后按实际 path 调整） */
export const CUSTOMER_DOWNLOAD_CENTER_PATH = "/system/downloadCenter";

export function customerDownloadCenterAction() {
    return createDownloadCenterAction(CUSTOMER_DOWNLOAD_CENTER_PATH);
}

type WarehouseSettingTab = "logistics" | "sku";

function warehouseViewAction(ctx: AsyncTaskContext, tab: WarehouseSettingTab): AsyncTaskNotifyAction {
    return {
        text: t("去查看"),
        to: {
            path: String(ctx.routePath || "/warehouse/setting"),
            query: { providerId: String(ctx.providerId ?? ""), tab },
        },
    };
}

function resolveTaskLabel(task: AsyncTaskDetail, fallback: string) {
    return task.taskTypeLabel?.trim() || fallback;
}

function buildWarehouseSyncFailed(task: AsyncTaskDetail, ctx: AsyncTaskContext, tab: WarehouseSettingTab): AsyncTaskNotifyContent {
    return {
        title: t("同步失败"),
        segments: [`${task.errorMessage || t("请重试")}，`, warehouseViewAction(ctx, tab)],
    };
}

function buildWarehouseSyncGiveUp(ctx: AsyncTaskContext, tab: WarehouseSettingTab, label: string): AsyncTaskNotifyContent {
    return {
        title: t("同步失败"),
        segments: [`${label}${t("任务执行失败，")}`, warehouseViewAction(ctx, tab)],
    };
}

// ---------- 导出（临时占位，后续重新定义） ----------
registerAsyncTaskPollingConfig(ASYNC_TASK_TYPE.Export, {
    notifyOnGiveUp: true,
});

registerAsyncTaskHandler(ASYNC_TASK_TYPE.Export, {
    buildPendingToast: () => t("已提交导出任务，请稍后在下载中心查看"),
    buildSuccess: task => ({
        title: t("导出完成"),
        segments: [`${resolveTaskLabel(task, t("导出"))}${t("已完成，")}`, customerDownloadCenterAction()],
    }),
    buildFailed: task => ({
        title: t("导出失败"),
        segments: [`${task.errorMessage || t("请重试")}，`, customerDownloadCenterAction()],
    }),
    buildGiveUp: (_ctx, label) => ({
        title: t("导出失败"),
        segments: [`${label?.trim() || t("导出")}${t("任务执行失败，")}`, customerDownloadCenterAction()],
    }),
});

// ---------- 仓库同步 ----------
registerAsyncTaskPollingConfig(WAREHOUSE_ASYNC_TASK_TYPE.ChannelSync, WAREHOUSE_SYNC_POLLING_CONFIG);
registerAsyncTaskPollingConfig(WAREHOUSE_ASYNC_TASK_TYPE.SkuSync, WAREHOUSE_SYNC_POLLING_CONFIG);
registerAsyncTaskPollingConfig(WAREHOUSE_ASYNC_TASK_TYPE.InventorySync, WAREHOUSE_SYNC_POLLING_CONFIG);

registerAsyncTaskHandler(WAREHOUSE_ASYNC_TASK_TYPE.ChannelSync, {
    buildPendingToast: () => t("已提交同步任务，请稍后查看结果"),
    buildSuccess: (task, ctx) => ({
        title: t("同步完成"),
        segments: [`${resolveTaskLabel(task, t("同步物流渠道"))}${t("已完成，")}`, warehouseViewAction(ctx, "logistics")],
    }),
    buildFailed: (task, ctx) => buildWarehouseSyncFailed(task, ctx, "logistics"),
    buildGiveUp: (ctx, label) => buildWarehouseSyncGiveUp(ctx, "logistics", label?.trim() || t("同步物流渠道")),
});

registerAsyncTaskHandler(WAREHOUSE_ASYNC_TASK_TYPE.SkuSync, {
    buildPendingToast: () => t("已提交同步任务，请稍后查看结果"),
    buildSuccess: (task, ctx) => ({
        title: t("同步完成"),
        segments: [`${resolveTaskLabel(task, t("同步海外仓SKU"))}${t("已完成，")}`, warehouseViewAction(ctx, "sku")],
    }),
    buildFailed: (task, ctx) => buildWarehouseSyncFailed(task, ctx, "sku"),
    buildGiveUp: (ctx, label) => buildWarehouseSyncGiveUp(ctx, "sku", label?.trim() || t("同步海外仓SKU")),
});

registerAsyncTaskHandler(WAREHOUSE_ASYNC_TASK_TYPE.InventorySync, {
    buildPendingToast: () => t("已提交库存同步任务，请稍后查看结果"),
    buildSuccess: (task, ctx) => ({
        title: t("库存同步完成"),
        segments: [
            ctx.warehouseSku ? `${t("SKU")} ${ctx.warehouseSku} ${resolveTaskLabel(task, t("同步库存"))}${t("已完成，")}` : `${resolveTaskLabel(task, t("同步库存"))}${t("已完成，")}`,
            warehouseViewAction(ctx, "sku"),
        ],
    }),
    buildFailed: (task, ctx) => buildWarehouseSyncFailed(task, ctx, "sku"),
    buildGiveUp: (ctx, label) => buildWarehouseSyncGiveUp(ctx, "sku", label?.trim() || t("同步库存")),
});
